import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  DEFAULT_HIVE_POLICY,
  hiveCalls,
  type ThinkerHiveMode,
  type ThinkerHivePolicy,
} from "~/api/aiAdminTypes";
import { useApi } from "~/api/provider";
import { Select } from "~/features/settings/controls";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";

/** The query key the editor and this section share for a thinker's hive policy. */
export const hiveQueryKey = (kind: "local" | "persona", id: string) =>
  ["thinkers", "hive", kind, id] as const;

/**
 * THE HIVE — the thinker's facets thinking the same thing at once. Owner (2026-10-10): "each
 * personality attached assesses the prompt and responds with its own point of view … the thinker
 * decides — by the personality marked as the authority — where the personalities have weight (an
 * internal voting system) … if it takes too long it is an impasse, and a human is involved."
 * Every change here PUTs at once (`…/hive`); the facets' own save is separate.
 */
export function HiveSettings({
  kind,
  id,
  policy,
  facetCount,
  hasAuthority,
}: {
  kind: "local" | "persona";
  id: string;
  /** The stored policy (the editor loads it); null while loading or when it could not be read. */
  policy: ThinkerHivePolicy | null;
  facetCount: number;
  hasAuthority: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const store = kind === "local" ? api.thinkers.localHive : api.thinkers.personaHive;
  const current = policy ?? DEFAULT_HIVE_POLICY;
  const save = useMutation({
    mutationFn: (next: ThinkerHivePolicy) => store.put(id, next),
    onSuccess: (saved) => {
      queryClient.setQueryData(hiveQueryKey(kind, id), saved);
      toast.success(saved.enabled ? t("Hive on") : t("Hive settings saved"));
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const change = (patch: Partial<ThinkerHivePolicy>) => save.mutate({ ...current, ...patch });
  const calls = hiveCalls(facetCount, current);
  return (
    <section
      className="flex flex-col gap-2 rounded border border-divider p-3"
      aria-label={t("Hive")}
    >
      <h4 className="font-medium">{t("Hive")}</h4>
      <label className="flex items-start gap-2 text-body text-text">
        <input
          type="checkbox"
          className="mt-1"
          checked={current.enabled}
          disabled={policy === null || save.isPending}
          onChange={(event) => change({ enabled: event.target.checked })}
        />
        <span>
          {t("Think as a hive — every facet answers, they vote, the authority decides")}
          <Text variant="small" as="span" className="block">
            {facetCount < 2
              ? t("A hive needs at least two facets.")
              : hasAuthority
                ? t("Impasses the facets cannot settle are brought to you.")
                : t("No authority yet: the heaviest facet decides. Mark one above.")}
          </Text>
        </span>
      </label>
      {current.enabled ? (
        <div className="flex flex-col gap-3">
          <Select
            label={t("How the facets think")}
            value={current.mode}
            fullWidth
            disabled={save.isPending}
            onChange={(event) => change({ mode: event.target.value as ThinkerHiveMode })}
          >
            <option value="parallel">{t("Parallel: all at once")}</option>
            <option value="turns">{t("In turns: each sees the earlier ones")}</option>
          </Select>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label={t("Debate rounds")}
              type="number"
              min={0}
              max={4}
              value={current.maxDebateRounds}
              fullWidth
              disabled={save.isPending}
              hint={t("0–4. How many times an impasse asks the others.")}
              onChange={(event) =>
                change({
                  maxDebateRounds: Math.min(4, Math.max(0, Number(event.target.value) || 0)),
                })
              }
            />
            <Input
              label={t("Impasses per thought")}
              type="number"
              min={0}
              max={6}
              value={current.maxImpasses}
              fullWidth
              disabled={save.isPending}
              hint={t("0–6. Past this, the rest is brought to you unsettled.")}
              onChange={(event) =>
                change({ maxImpasses: Math.min(6, Math.max(0, Number(event.target.value) || 0)) })
              }
            />
            <Input
              label={t("Words per facet")}
              type="number"
              min={40}
              max={400}
              step={10}
              value={current.facetWords}
              fullWidth
              disabled={save.isPending}
              hint={t("40–400.")}
              onChange={(event) =>
                change({
                  facetWords: Math.min(400, Math.max(40, Number(event.target.value) || 40)),
                })
              }
            />
          </div>
          <Text variant="small">
            {t("about {{calls}} calls per thought on a local model", { calls })}
          </Text>
        </div>
      ) : null}
    </section>
  );
}
